// TableEditor / TablePlayer — Tipo de pregunta "tabla".
//
// Schema (dentro de `q`):
//   q.type === "table_input"
//   q.rows, q.cols    → enteros (3×3 por defecto)
//   q.cells           → matriz [rows][cols] de objetos { text, editable, correct_answer }
//                       Si `editable=true`, el alumno verá un input en esa celda.
//                       Si `correct_answer` está definido, se grada con igualdad
//                       (numérica si parseable, si no string-insensible).
//
// La respuesta del alumno: { "r0c0": "5", "r1c2": "x+1", ... } indexada por celda.
import { Plus, Trash2, Upload } from "lucide-react";
import { useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export function makeEmptyTable(rows = 3, cols = 3) {
  return Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => ({
      text: r === 0 ? `Col ${c + 1}` : (c === 0 ? `Fila ${r}` : ""),
      editable: r > 0 && c > 0,
      correct_answer: "",
    }))
  );
}

export function TableEditor({ q, onUpdate, templateCode }) {
  const fileRef = useRef(null);
  const rows = q.rows || (q.cells?.length ?? 3);
  const cols = q.cols || (q.cells?.[0]?.length ?? 3);
  const cells = q.cells && q.cells.length ? q.cells : makeEmptyTable(rows, cols);

  const setCell = (r, c, patch) => {
    const next = cells.map((row, ri) => row.map((cell, ci) => (ri === r && ci === c ? { ...cell, ...patch } : cell)));
    onUpdate({ rows, cols, cells: next });
  };

  const addRow = () => {
    const newRow = Array.from({ length: cols }, (_, c) => ({
      text: c === 0 ? `Fila ${rows}` : "",
      editable: c > 0,
      correct_answer: "",
    }));
    onUpdate({ rows: rows + 1, cols, cells: [...cells, newRow] });
  };

  const addCol = () => {
    const next = cells.map((row, r) => [
      ...row,
      { text: r === 0 ? `Col ${cols + 1}` : "", editable: r > 0, correct_answer: "" },
    ]);
    onUpdate({ rows, cols: cols + 1, cells: next });
  };

  const removeRow = (r) => {
    if (rows <= 1) return;
    const next = cells.filter((_, i) => i !== r);
    onUpdate({ rows: rows - 1, cols, cells: next });
  };

  const removeCol = (c) => {
    if (cols <= 1) return;
    const next = cells.map((row) => row.filter((_, i) => i !== c));
    onUpdate({ rows, cols: cols - 1, cells: next });
  };

  const uploadExcel = async (file) => {
    if (!file || !templateCode) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        "/questionnaire-templates/_/parse-table-excel",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (!data.cells || !data.cells.length) throw new Error("Excel vacío");
      onUpdate({ rows: data.rows, cols: data.cols, cells: data.cells });
      toast.success(`Tabla cargada: ${data.rows} × ${data.cols}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error procesando el Excel");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2" data-testid={`table-editor-${q.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">Tabla {rows} × {cols}</span>
        <button type="button" onClick={addRow} className="text-[11px] font-bold inline-flex items-center gap-1 bg-white border-2 border-[#E1E5F0] hover:border-[#A43790] rounded px-2 py-0.5"
          data-testid={`table-add-row-${q.id}`}>
          <Plus size={10} /> Fila
        </button>
        <button type="button" onClick={addCol} className="text-[11px] font-bold inline-flex items-center gap-1 bg-white border-2 border-[#E1E5F0] hover:border-[#A43790] rounded px-2 py-0.5"
          data-testid={`table-add-col-${q.id}`}>
          <Plus size={10} /> Columna
        </button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
          onChange={(e) => uploadExcel(e.target.files?.[0])} />
        <button type="button" onClick={() => fileRef.current?.click()}
          className="text-[11px] font-bold inline-flex items-center gap-1 bg-[#2C49A0] text-white rounded px-2 py-0.5 hover:bg-[#1F2547]"
          data-testid={`table-upload-excel-${q.id}`}>
          <Upload size={10} /> Cargar Excel/CSV
        </button>
      </div>
      <p className="text-[10px] text-[#6E7591] italic">
        Marca «Editable» en las celdas donde el alumno tiene que responder. Si defines una «Respuesta esperada», la celda se evaluará automáticamente.
      </p>
      <div className="overflow-x-auto">
        <table className="border-collapse" data-testid={`table-grid-${q.id}`}>
          <tbody>
            {cells.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td key={c} className={`p-1 border-2 align-top ${r === 0 || c === 0 ? "bg-[#F7F8FA]" : "bg-white"} border-[#E1E5F0]`}>
                    <input type="text" value={cell.text || ""}
                      onChange={(e) => setCell(r, c, { text: e.target.value })}
                      data-testid={`table-cell-text-${q.id}-${r}-${c}`}
                      placeholder={r === 0 ? "Header" : (c === 0 ? "Etiqueta" : "Contenido")}
                      className={`w-28 px-1.5 py-0.5 text-xs ${r === 0 || c === 0 ? "font-bold" : ""} bg-transparent border-b border-transparent focus:border-[#A43790] outline-none`} />
                    {r > 0 && c > 0 && (
                      <div className="mt-1 space-y-1">
                        <label className="inline-flex items-center gap-1 text-[9px] font-bold text-[#4C5478]">
                          <input type="checkbox" checked={!!cell.editable}
                            onChange={(e) => setCell(r, c, { editable: e.target.checked })}
                            data-testid={`table-cell-editable-${q.id}-${r}-${c}`} />
                          Editable
                        </label>
                        {cell.editable && (
                          <input type="text" value={cell.correct_answer || ""}
                            onChange={(e) => setCell(r, c, { correct_answer: e.target.value })}
                            placeholder="Resp. esperada"
                            data-testid={`table-cell-correct-${q.id}-${r}-${c}`}
                            className="w-full px-1.5 py-0.5 text-[10px] rounded border border-[#3FA84D] text-[#3FA84D] font-mono outline-none" />
                        )}
                      </div>
                    )}
                  </td>
                ))}
                {r > 0 && (
                  <td className="border-0 pl-1">
                    <button type="button" onClick={() => removeRow(r)}
                      data-testid={`table-remove-row-${q.id}-${r}`}
                      className="text-[#A52258] hover:text-[#D43790]"><Trash2 size={12} /></button>
                  </td>
                )}
              </tr>
            ))}
            <tr>
              {Array.from({ length: cols }, (_, c) => (
                <td key={c} className="text-center py-0.5">
                  {c > 0 && (
                    <button type="button" onClick={() => removeCol(c)}
                      data-testid={`table-remove-col-${q.id}-${c}`}
                      className="text-[#A52258] hover:text-[#D43790]"><Trash2 size={10} /></button>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function TablePlayer({ q, value, onChange }) {
  const cells = q.cells || [];
  const vals = (typeof value === "object" && value !== null) ? value : {};
  const setCell = (r, c, v) => onChange({ ...vals, [`r${r}c${c}`]: v });
  return (
    <div className="overflow-x-auto" data-testid={`table-player-${q.id}`}>
      <table className="border-collapse">
        <tbody>
          {cells.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td key={c} className={`p-2 border-2 ${r === 0 || c === 0 ? "bg-[#F7F8FA] font-bold" : "bg-white"} border-[#E1E5F0]`}>
                  {cell.editable ? (
                    <input type="text"
                      value={vals[`r${r}c${c}`] ?? ""}
                      onChange={(e) => setCell(r, c, e.target.value)}
                      data-testid={`table-input-${q.id}-${r}-${c}`}
                      placeholder="?"
                      className="w-24 px-2 py-1 rounded border-2 border-[#A43790] bg-[#FFF1F8] text-[#1F2547] outline-none focus:bg-white text-sm" />
                  ) : (
                    <span className="text-sm text-[#1F2547]">{cell.text}</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
